import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Logo from '@/components/Logo';
import {
  Users,
  MapPin,
  Wallet,
  Clock,
  CheckCircle,
  ArrowRight,
  Phone,
  Mail,
  Globe,
  Target,
  TrendingUp,
  Shield,
  Smartphone,
  MessageCircle,
  Star,
  Handshake,
  Award,
  Heart,
  Send
} from 'lucide-react';
import { toast } from 'sonner';

const WHATSAPP_NUMBER = '+15144438084';

const JoinAgents = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    city: '',
    experience: '',
    motivation: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Structure de rémunération
  const earnings = {
    bonusInscription: 5000, // FCFA par inscription
    commissionMensuelle: 20, // % sur abonnements
  };

  const benefits = [
    {
      icon: Wallet,
      title: '5 000 FCFA / inscription',
      description: 'Bonus fixe pour chaque professionnel ou établissement qui finalise son inscription sur DOCKHTA.'
    },
    {
      icon: TrendingUp,
      title: '20% de commission récurrente',
      description: 'Recevez 20% sur chaque paiement mensuel de vos recrues tant qu\'ils restent actifs sur la plateforme.'
    },
    {
      icon: Clock,
      title: 'Flexibilité totale',
      description: 'Travaillez à votre rythme, gérez votre emploi du temps comme vous le souhaitez.'
    },
    {
      icon: Smartphone,
      title: 'Dashboard de suivi',
      description: 'Application dédiée pour gérer vos recrutements, suivre vos commissions et demander vos retraits.'
    },
    {
      icon: Shield,
      title: 'Formation complète',
      description: 'Formation gratuite sur la plateforme, les techniques de recrutement et la santé digitale.'
    },
    {
      icon: Handshake,
      title: 'Accompagnement continu',
      description: 'Équipe support dédiée pour vous aider dans vos missions quotidiennes.'
    }
  ];

  const missions = [
    {
      icon: Users,
      title: 'Recrutement de professionnels',
      description: 'Identifier et convaincre médecins, infirmiers, pharmaciens de rejoindre DOCKHTA.'
    },
    {
      icon: MapPin,
      title: 'Couverture territoriale',
      description: 'Développer la présence de DOCKHTA dans votre ville et ses environs.'
    },
    {
      icon: Target,
      title: 'Vérification KYC',
      description: 'Valider les documents et identités des professionnels de santé recrutés.'
    },
    {
      icon: Heart,
      title: 'Accompagnement patients',
      description: 'Aider les patients à créer leur compte et prendre leurs premiers rendez-vous.'
    }
  ];

  // Conditions de présélection
  const requirements = [
    {
      icon: Smartphone,
      text: 'Être à l\'aise avec un smartphone'
    },
    {
      icon: Globe,
      text: 'Savoir naviguer sur internet'
    },
    {
      icon: Users,
      text: 'Avoir un bon sens du relationnel'
    },
    {
      icon: MapPin,
      text: 'Résider au Cameroun'
    }
  ];

  const testimonials = [
    {
      name: 'Jean-Paul K.',
      city: 'Douala',
      quote: 'En 3 mois, j\'ai recruté plus de 50 professionnels et généré un revenu confortable tout en gardant mon emploi principal.',
      avatar: 'JP'
    },
    {
      name: 'Marie-Claire N.',
      city: 'Yaoundé',
      quote: 'L\'équipe DOCKHTA m\'a formée et accompagnée. Aujourd\'hui je suis responsable de toute la région Centre.',
      avatar: 'MC'
    },
    {
      name: 'Samuel T.',
      city: 'Bafoussam',
      quote: 'Ce qui me motive c\'est d\'améliorer l\'accès aux soins dans ma communauté tout en gagnant ma vie.',
      avatar: 'ST'
    }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.fullName || !formData.phone || !formData.city) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    setSubmitting(true);

    // Construire le message WhatsApp
    const message = encodeURIComponent(
      `Bonjour DOCKHTA!\n\n` +
      `Je souhaite devenir Agent Terrain.\n\n` +
      `*Mes informations:*\n` +
      `Nom: ${formData.fullName}\n` +
      `Téléphone: ${formData.phone}\n` +
      `Email: ${formData.email || 'Non renseigné'}\n` +
      `Ville: ${formData.city}\n` +
      `Expérience: ${formData.experience || 'Non renseignée'}\n\n` +
      `*Motivation:*\n${formData.motivation || 'Non renseignée'}\n\n` +
      `_Envoyé depuis le formulaire de recrutement Agents Terrain DOCKHTA_`
    );

    // Rediriger vers WhatsApp
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}?text=${message}`;
    window.open(whatsappUrl, '_blank');

    toast.success('Redirection vers WhatsApp...');
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="join-agents-page">
      {/* Navigation */}
      <nav className="bg-white/95 backdrop-blur-md border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/">
            <Logo size="sm" showSlogan={false} />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-slate-600" data-testid="login-btn">
                Connexion
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-[#E53935] hover:bg-[#C62828] rounded-xl" data-testid="register-btn">
                S'inscrire
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#1A237E] via-[#283593] to-[#3949AB] text-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,...')] opacity-10" />
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <Star className="w-5 h-5 text-yellow-400" />
                <span className="text-sm font-medium">Opportunité de carrière</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Devenez <span className="text-[#E53935]">Agent Terrain</span> DOCKHTA
              </h1>
              
              <p className="text-xl text-white/90 mb-8 max-w-xl">
                Rejoignez notre réseau d'ambassadeurs et participez à la révolution de la santé digitale en Afrique. 
                Gagnez des revenus attractifs tout en ayant un impact positif sur votre communauté.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={() => document.getElementById('form-section').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-[#E53935] hover:bg-[#C62828] text-white rounded-xl h-14 px-8 text-lg shadow-xl"
                  data-testid="cta-postuler"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Postuler maintenant
                </Button>
                <a 
                  href={`https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}?text=${encodeURIComponent('Bonjour, je souhaite en savoir plus sur le poste d\'Agent Terrain DOCKHTA.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10 rounded-xl h-14 px-8 text-lg w-full sm:w-auto"
                    data-testid="cta-whatsapp"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Contacter via WhatsApp
                  </Button>
                </a>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 mt-12">
                <div>
                  <p className="text-3xl font-bold text-white">100+</p>
                  <p className="text-sm text-white/70">Agents actifs</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">500+</p>
                  <p className="text-sm text-white/70">Pros recrutés</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">10</p>
                  <p className="text-sm text-white/70">Régions couvertes</p>
                </div>
              </div>
            </div>

            {/* Image/Illustration */}
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-[#E53935]/20 to-transparent rounded-3xl" />
              <img 
                src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=600&q=80"
                alt="Agent Terrain DOCKHTA"
                className="w-full h-auto rounded-3xl shadow-2xl"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=600&q=80';
                }}
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">+150%</p>
                    <p className="text-sm text-slate-500">Croissance réseau</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Revenus Section - Calculateur */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A237E] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Combien pouvez-vous gagner ?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Structure de rémunération transparente et attractive
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Bonus inscription */}
            <Card className="border-0 shadow-xl bg-gradient-to-br from-green-500 to-green-600 text-white overflow-hidden relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <CardContent className="p-8 relative">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
                    <Award className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <p className="text-white/80 text-sm">Bonus par inscription</p>
                    <p className="text-4xl font-bold">5 000 FCFA</p>
                  </div>
                </div>
                <p className="text-white/90">
                  Recevez <strong>5 000 FCFA</strong> pour chaque professionnel ou établissement 
                  qui finalise son inscription sur DOCKHTA grâce à votre invitation.
                </p>
                <div className="mt-4 pt-4 border-t border-white/20">
                  <p className="text-sm text-white/70">
                    Exemple: 10 inscriptions = <strong className="text-white">50 000 FCFA</strong>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Commission récurrente */}
            <Card className="border-0 shadow-xl bg-gradient-to-br from-[#1976D2] to-[#1565C0] text-white overflow-hidden relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <CardContent className="p-8 relative">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
                    <TrendingUp className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <p className="text-white/80 text-sm">Commission mensuelle</p>
                    <p className="text-4xl font-bold">20%</p>
                  </div>
                </div>
                <p className="text-white/90">
                  Touchez <strong>20%</strong> sur chaque paiement d'abonnement mensuel 
                  de vos recrues, tant qu'ils restent actifs sur la plateforme.
                </p>
                <div className="mt-4 pt-4 border-t border-white/20">
                  <p className="text-sm text-white/70">
                    Exemple: 10 pros à 10 000 FCFA/mois = <strong className="text-white">20 000 FCFA/mois</strong> récurrent
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Exemple de revenus */}
          <Card className="border-0 shadow-lg bg-slate-50">
            <CardContent className="p-8">
              <h3 className="text-xl font-semibold text-[#1A237E] mb-6 text-center">
                Simulation de revenus mensuels
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-slate-600 font-medium">Activité</th>
                      <th className="text-center py-3 px-4 text-slate-600 font-medium">Débutant</th>
                      <th className="text-center py-3 px-4 text-slate-600 font-medium">Actif</th>
                      <th className="text-center py-3 px-4 text-slate-600 font-medium">Performant</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-slate-100">
                      <td className="py-3 px-4 text-slate-700">Inscriptions / mois</td>
                      <td className="text-center py-3 px-4 font-medium">5</td>
                      <td className="text-center py-3 px-4 font-medium">15</td>
                      <td className="text-center py-3 px-4 font-medium">30</td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-3 px-4 text-slate-700">Bonus inscriptions</td>
                      <td className="text-center py-3 px-4 text-green-600 font-medium">25 000 FCFA</td>
                      <td className="text-center py-3 px-4 text-green-600 font-medium">75 000 FCFA</td>
                      <td className="text-center py-3 px-4 text-green-600 font-medium">150 000 FCFA</td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-3 px-4 text-slate-700">Recrues actives (cumul)</td>
                      <td className="text-center py-3 px-4 font-medium">10</td>
                      <td className="text-center py-3 px-4 font-medium">50</td>
                      <td className="text-center py-3 px-4 font-medium">100</td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-3 px-4 text-slate-700">Commissions (20%)</td>
                      <td className="text-center py-3 px-4 text-blue-600 font-medium">20 000 FCFA</td>
                      <td className="text-center py-3 px-4 text-blue-600 font-medium">100 000 FCFA</td>
                      <td className="text-center py-3 px-4 text-blue-600 font-medium">200 000 FCFA</td>
                    </tr>
                    <tr className="bg-[#1A237E]/5">
                      <td className="py-4 px-4 font-bold text-[#1A237E]">Total mensuel</td>
                      <td className="text-center py-4 px-4 font-bold text-[#E53935]">45 000 FCFA</td>
                      <td className="text-center py-4 px-4 font-bold text-[#E53935]">175 000 FCFA</td>
                      <td className="text-center py-4 px-4 font-bold text-[#E53935]">350 000 FCFA</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-500 text-center mt-4">
                * Basé sur un abonnement moyen de 10 000 FCFA/mois par professionnel
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Avantages Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A237E] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Pourquoi devenir Agent Terrain ?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Des avantages concrets pour une activité flexible et rémunératrice
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-0 shadow-md hover:shadow-xl transition-all duration-300 group">
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#E53935] to-[#C62828] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <benefit.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{benefit.title}</h3>
                  <p className="text-slate-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Missions Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A237E] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Vos missions
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Un rôle clé dans l'écosystème DOCKHTA
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-8">
            {missions.map((mission, index) => (
              <div key={index} className="flex gap-4 p-6 rounded-2xl bg-slate-50 hover:bg-slate-100 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-[#1A237E] flex items-center justify-center flex-shrink-0">
                  <mission.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">{mission.title}</h3>
                  <p className="text-slate-600">{mission.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Témoignages */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1A237E] to-[#3949AB] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Ils ont rejoint l'aventure
            </h2>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Découvrez les témoignages de nos agents terrain
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-sm border-0">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-[#E53935] flex items-center justify-center text-white font-bold">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <p className="font-semibold text-white">{testimonial.name}</p>
                      <p className="text-sm text-white/70 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {testimonial.city}
                      </p>
                    </div>
                  </div>
                  <p className="text-white/90 italic">"{testimonial.quote}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Formulaire de candidature */}
      <section id="form-section" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A237E] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Postuler maintenant
            </h2>
            <p className="text-lg text-slate-600">
              Ce formulaire constitue une <strong>présélection</strong>. Nous vous contacterons via WhatsApp.
            </p>
          </div>

          {/* Processus de sélection */}
          <Card className="border-0 shadow-lg bg-gradient-to-r from-amber-50 to-orange-50 mb-8">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-[#1A237E] mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-600" />
                Processus de sélection
              </h3>
              
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="flex items-center gap-3 bg-white rounded-xl p-4">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold">1</div>
                  <div>
                    <p className="font-medium text-slate-800">Candidature</p>
                    <p className="text-xs text-slate-500">Formulaire ci-dessous</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-white rounded-xl p-4">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold">2</div>
                  <div>
                    <p className="font-medium text-slate-800">Formation gratuite</p>
                    <p className="text-xs text-slate-500">1 heure en ligne</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-white rounded-xl p-4">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold">
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Validation</p>
                    <p className="text-xs text-slate-500">Accès au tableau de bord</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-amber-200 pt-4">
                <p className="text-sm font-medium text-slate-700 mb-3">Conditions requises :</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {requirements.map((req, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm text-slate-600">
                      <req.icon className="w-4 h-4 text-amber-600" />
                      <span>{req.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-xl">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="fullName" className="text-slate-700">Nom complet *</Label>
                    <Input
                      id="fullName"
                      value={formData.fullName}
                      onChange={(e) => setFormData(p => ({ ...p, fullName: e.target.value }))}
                      placeholder="Votre nom complet"
                      className="mt-2 h-12 rounded-xl"
                      required
                      data-testid="input-fullname"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-slate-700">Téléphone *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))}
                      placeholder="+237 6XX XXX XXX"
                      className="mt-2 h-12 rounded-xl"
                      required
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="email" className="text-slate-700">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                      placeholder="votre@email.com"
                      className="mt-2 h-12 rounded-xl"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city" className="text-slate-700">Ville de résidence *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData(p => ({ ...p, city: e.target.value }))}
                      placeholder="Ex: Douala, Yaoundé..."
                      className="mt-2 h-12 rounded-xl"
                      required
                      data-testid="input-city"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="experience" className="text-slate-700">Expérience pertinente</Label>
                  <Select
                    value={formData.experience}
                    onValueChange={(v) => setFormData(p => ({ ...p, experience: v }))}
                  >
                    <SelectTrigger className="mt-2 h-12 rounded-xl" data-testid="select-experience">
                      <SelectValue placeholder="Sélectionnez votre expérience" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune expérience similaire</SelectItem>
                      <SelectItem value="sales">Commercial / Vente</SelectItem>
                      <SelectItem value="health">Secteur santé</SelectItem>
                      <SelectItem value="community">Agent communautaire</SelectItem>
                      <SelectItem value="marketing">Marketing / Communication</SelectItem>
                      <SelectItem value="other">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="motivation" className="text-slate-700">Votre motivation</Label>
                  <Textarea
                    id="motivation"
                    value={formData.motivation}
                    onChange={(e) => setFormData(p => ({ ...p, motivation: e.target.value }))}
                    placeholder="Pourquoi souhaitez-vous devenir Agent Terrain DOCKHTA ?"
                    className="mt-2 rounded-xl"
                    rows={4}
                    data-testid="textarea-motivation"
                  />
                </div>

                <div className="bg-green-50 rounded-xl p-4 flex items-start gap-3">
                  <MessageCircle className="w-6 h-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-green-900">Candidature via WhatsApp</p>
                    <p className="text-sm text-green-700">
                      En cliquant sur "Envoyer ma candidature", vous serez redirigé vers WhatsApp 
                      pour finaliser votre candidature avec notre équipe de recrutement.
                    </p>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-14 bg-[#E53935] hover:bg-[#C62828] rounded-xl text-lg font-semibold"
                  data-testid="submit-btn"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Envoyer ma candidature
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact direct */}
          <div className="mt-8 text-center">
            <p className="text-slate-600 mb-4">Ou contactez-nous directement :</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href={`https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-green-600 hover:text-green-700 font-medium"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp: {WHATSAPP_NUMBER}
              </a>
              <span className="hidden sm:inline text-slate-300">|</span>
              <a 
                href="mailto:agents@dockhta.com"
                className="flex items-center gap-2 text-[#1976D2] hover:text-[#1565C0] font-medium"
              >
                <Mail className="w-5 h-5" />
                agents@dockhta.com
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0D1B2A] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Logo size="sm" variant="white" showSlogan={false} />
            <div className="flex items-center gap-6">
              <Link to="/" className="text-slate-400 hover:text-white text-sm">Accueil</Link>
              <Link to="/rejoindre" className="text-slate-400 hover:text-white text-sm">Professionnels</Link>
              <Link to="/contact" className="text-slate-400 hover:text-white text-sm">Contact</Link>
            </div>
            <p className="text-slate-500 text-sm">© 2026 DOCKHTA</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default JoinAgents;
